import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Flex from '../../../components/base/Flex';
import OutlinedButton from '../../../components/base/OutlinedButton';
import CustomModal from '../../../components/base/CustomModal';
import SeleniumSvg from '../../../components/svg/SeleniumSvg';
import PlaywrightSvg from '../../../components/svg/PlaywrightSvg';
import PuppeteerSvg from '../../../components/svg/PuppeteerSvg';
import ScandiumLogoSolid from '../../../components/svg/ScandiumLogoSolid';
import PageLoader from '../../../components/PageLoader';
import TextButton from '../../../components/base/TextButton';
import CopySvg from '../../../components/svg/CopySvg';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { toast } from 'react-toastify';
import { useTheme } from '@emotion/react';

const tabValues = [
  { label: 'Scandium', value: 'scandium', icon: <ScandiumLogoSolid width={22} height={22} /> },
  { label: 'Selenium', value: 'selenium', icon: <SeleniumSvg /> },
  { label: 'Playwright', value: 'playwright', icon: <PlaywrightSvg /> },
  { label: 'Puppeteer', value: 'puppeteer', icon: <PuppeteerSvg /> },
  { label: 'AI Prompt', value: 'ai-prompt', icon: <span>🤖</span> }
];

const generatePromptFromSteps = (steps) => {
  if (!Array.isArray(steps)) {
    throw new Error('Steps must be an array');
  }

  const actions = [];

  const getLabel = (element = {}) => {
    return (
      element?.placeholder ||
      element?.text ||
      element?.title ||
      element?.name ||
      element?.id ||
      element?.customSelector?.[0] ||
      element?.tagName?.toLowerCase() ||
      'element'
    );
  };

  const formatAssertion = (step) => {
    const label = getLabel(step.element);
    const expected = step.expectedValue;
    const prop = step.expectedPropertyName;

    switch (step.subtype) {
      case 'page-title':
        return `verify page title is "${expected}"`;

      case 'page-url':
        return `verify page URL is "${expected}"`;

      case 'page-text':
        return `verify page contains text "${expected}"`;

      case 'element-visible':
        return `verify "${label}" is visible`;

      case 'element-not-visible':
        return `verify "${label}" is not visible`;

      case 'element-contains-text':
        return `verify "${label}" contains text "${expected}"`;

      case 'element-not-contains-text':
        return `verify "${label}" does not contain text "${expected}"`;

      case 'checkbox-checked':
        return `verify checkbox "${label}" is checked`;

      case 'element-enabled':
        return `verify "${label}" is enabled`;

      case 'element-disabled':
        return `verify "${label}" is disabled`;

      case 'element-html-property':
        return `verify "${label}" HTML property "${prop}" equals "${expected}"`;

      case 'element-css-property':
        return `verify "${label}" CSS property "${prop}" equals "${expected}"`;

      case 'selected-label':
        return `verify "${label}" selected label is "${expected}"`;

      case 'selected-value':
        return `verify "${label}" selected value is "${expected}"`;

      default:
        return `verify "${label}" matches expected assertion`;
    }
  };

  for (const step of steps) {
    const { type, page = {}, element = {}, files = [], x, y } = step;

    switch (type) {
      case 'visit':
        actions.push(`visit "${page.url}"`);
        break;

      case 'click':
        actions.push(`click "${getLabel(element)}"`);
        break;

      case 'change':
      case 'textInput':
      case 'editcontent':
        actions.push(`type "${element.value || element.text || ''}" into "${getLabel(element)}"`);
        break;

      case 'file-drop':
      case 'file-input':
        actions.push(`upload "${files.map((file) => file.url).join(', ')}"`);
        break;

      case 'scroll':
        if (y) {
          actions.push(`Scroll down the page by ${y} pixels`);
        } else {
          actions.push(`Scroll to "${getLabel(element)}"`);
        }
        break;

      case 'hover':
        actions.push(`Hover over "${getLabel(element)}"`);
        break;

      case 'assert':
        actions.push(formatAssertion(step));
        break;

      case 'specialkey':
        actions.push(step?.title);
        break;

      case 'draganddrop':
        actions.push(step?.title);
        break;

      default:
        actions.push(`"${type}" on "${getLabel(element)}"`);
    }
  }

  return actions.map((action, index) => `${index + 1}. ${action}`).join('\n');
};

const ExportTestModal = ({
  open,
  onClose,
  onComplete,
  activeTab,
  setActiveTab,
  handleConvertTestcase,
  isLoading,
  convertedCodes,
  events
}) => {
  const stringifiedEvents = JSON.stringify(events, null, 2);
  const theme = useTheme();

  const getCurrentContent = () => {
    if (activeTab === 'scandium') {
      return stringifiedEvents;
    } else if (activeTab === 'ai-prompt') {
      return generatePromptFromSteps(events);
    } else {
      return convertedCodes?.data?.code || '';
    }
  };

  const getLanguageForHighlighting = () => {
    if (activeTab === 'ai-prompt') {
      return 'markdown';
    }
    return 'javascript';
  };

  const copyTestIdToClipBoard = () => {
    const content = getCurrentContent();
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  return (
    <CustomModal open={open}>
      <Box
        py={1}
        px={2}
        width={'80vw'}
        maxWidth={'700px'}
        minHeight={'400px'}
        position={'relative'}>
        <Typography
          as={'h4'}
          color={theme.palette.text.custom}
          sx={{
            fontSize: '1.4rem'
          }}>
          Test Case Conversion
        </Typography>

        <Typography
          as={'h4'}
          mb={2}
          color={theme.palette.text.black_grey}
          sx={{
            fontSize: '0.9rem'
          }}>
          Your Scandium test case can be converted for use with Selenium, Playwright, Puppeteer, or
          as an AI-readable prompt. Select and copy the framework you want to use.
        </Typography>

        <Flex
          sx={{ borderBottom: `3px solid ${theme.palette.table.outline}` }}
          alignItems={'center'}
          justifyContent={'space-between'}>
          <Tabs
            value={activeTab}
            onChange={async (event, newTab) => {
              await setActiveTab(newTab);
              if (newTab !== 'scandium' && newTab !== 'ai-prompt') {
                handleConvertTestcase();
              }
            }}
            aria-label={'Test cases tabs'}
            indicatorColor={'primary'}
            TabIndicatorProps={{
              sx: { height: 3 }
            }}>
            {tabValues.map((tab, i) => (
              <Tab
                key={i}
                label={
                  <Flex columnGap={2}>
                    {tab.icon}{' '}
                    <Typography variant="body1" color={theme.palette.text.custom}>
                      {tab.label}
                    </Typography>
                  </Flex>
                }
                value={tab.value}
                sx={{ textTransform: 'none', textAlign: 'left' }}
              />
            ))}
          </Tabs>
        </Flex>

        {(getCurrentContent() || (!isLoading && activeTab === 'ai-prompt')) && !isLoading && (
          <SyntaxHighlighter
            language={getLanguageForHighlighting()}
            showLineNumbers={true}
            wrapLines={true}
            customStyle={{
              maxHeight: '350px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
            style={dracula}>
            {getCurrentContent()}
          </SyntaxHighlighter>
        )}

        {isLoading && <PageLoader height={'150px'} />}

        <Flex sx={{ flex: 1, pt: 2 }} columnGap={2} justifyContent={'space-between'}>
          <TextButton
            onClick={copyTestIdToClipBoard}
            startIcon={<CopySvg fill={theme.palette.svg.primary} />}>
            Copy
          </TextButton>
          <OutlinedButton onClick={onClose}>Close</OutlinedButton>
        </Flex>
      </Box>
    </CustomModal>
  );
};

export default ExportTestModal;
